// A themed stand-in for window.confirm().
//
// Returns a promise like the browser's own, so call sites keep the shape
// they already had:
//
//   const { confirm, confirmDialog } = useConfirm();
//   if (!(await confirm({ title: 'Delete this order?' }))) return;
//   ...
//   return (<>{confirmDialog}{rest}</>);
import React, { useCallback, useRef, useState } from 'react';
import Modal from '../components/Modal';

export interface ConfirmOptions {
  title: string;
  /** Optional detail under the title — consequences, mostly. */
  body?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** 'danger' for anything that destroys something. */
  tone?: 'default' | 'danger';
}

export const useConfirm = () => {
  const [options, setOptions] = useState<ConfirmOptions | undefined>();
  // Held across renders so the buttons can settle the promise the caller
  // is already waiting on.
  const resolver = useRef<((ok: boolean) => void) | undefined>(undefined);

  const confirm = useCallback((opts: ConfirmOptions) => {
    setOptions(opts);
    return new Promise<boolean>(resolve => {
      resolver.current = resolve;
    });
  }, []);

  const settle = (ok: boolean) => {
    setOptions(undefined);
    resolver.current?.(ok);
    resolver.current = undefined;
  };

  const confirmDialog = options ? (
    <Modal
      title={options.title}
      onClose={() => settle(false)}
      footer={
        <>
          <button
            type="button"
            onClick={() => settle(false)}
            className="border border-background-dark rounded px-4 py-2 text-sm hover:border-primary"
          >
            {options.cancelLabel ?? 'Cancel'}
          </button>
          <button
            type="button"
            onClick={() => settle(true)}
            className={
              options.tone === 'danger'
                ? 'bg-red-600 text-white rounded px-4 py-2 text-sm font-medium hover:bg-red-700'
                : 'bg-primary text-white rounded px-4 py-2 text-sm font-medium hover:bg-primary-dark'
            }
          >
            {options.confirmLabel ?? 'Confirm'}
          </button>
        </>
      }
    >
      {options.body ?? 'This cannot be undone.'}
    </Modal>
  ) : null;

  return { confirm, confirmDialog };
};
